import { Injectable } from '@angular/core';
import {
  Account,
  AccountIssuance,
  AccountUser,
  ActiveStatusEnum,
  ApprovalTreeUserGroup,
  CollectionScopeEnum,
  JobOrder,
  JobOrderStatusEnum,
  Program,
  ProgramAllowance,
  ProgramAllowanceUserLog,
  ProgramTypeEnum,
  ProgramUserGroupMap,
} from '@models';
import { AccountDbQueryService } from './account-db-query.service';
import { DateFns, UtilFns } from '@utils';
import * as _ from 'lodash';
import { AuthService } from '@core';

export interface OrderIssue {
  message: string;
  details?: string[];
  programId?: string;
}

export interface ProgramUserItem {
  program: Program;
  accountUser: AccountUser;
  approvalTreeUserGroup: ApprovalTreeUserGroup;
  programUserGroupMap: ProgramUserGroupMap;
  err: string | null;
}

export interface IssuanceDetail {
  name: string;
  minQty: number | null;
  maxQty: number | null;
}

export interface AccountIssuanceDetail {
  name: string;
  timeframeDays: number;
  maxQty: number;
}

export interface PlaceOrderCardItem {
  accessItem: ProgramAccessItem;
  isAvailable: boolean;
  existingJo: JobOrder | null;
  statusLabel: string;
  buttonLabel: string;
  buttonIcon: string;
}

export interface ProgramAccessItem {
  program: Program;
  programUserGroupId: string;
  programUserGroupName: string;
  programUserGroupStatus: string;
  programUserGroupActiveStatusId: ActiveStatusEnum;
  joinDate: Date | null;
  isExcluded: boolean;
  isExpired: boolean;
  displayStatus: string;
  isIssuanceProgram: boolean;
  isAllowanceProgram: boolean;
  // Program type description (e.g., "Issuance", "Allowance", "Both", "Inventory", "None")
  programType: string;
  // Admin names for this program user group in the approval tree
  adminNames: string[];
  // Issuance categories that apply to this user group
  issuanceCategories: string[];
  // Issuance details with min/max quantities
  issuanceDetails: IssuanceDetail[];
  // Account issuance details with timeframe and max quantities
  accountIssuanceDetails: AccountIssuanceDetail[];
  // Allowance names that apply to this user group
  allowanceNames: string[];
  // Allowance fields (only populated if isAllowanceProgram is true)
  allowanceAmt: number | null;
  chargedAmt: number | null;
  adjustmentsAmt: number | null;
  // Program allowance only: allowanceAmt - chargedAmt + adjustmentsAmt.
  programBalanceAmt: number | null;
  // The user's global (cross-program) pot — see globalAllowanceAvailableAmt() for the sign rules.
  // The same pot backs every allowance program, so this repeats across a user's items and can only
  // be spent once. Negative when the pot is overdrawn.
  globalAllowanceAmt: number;
  // What the user can actually spend here: programBalanceAmt + globalAllowanceAmt. Matches
  // ProgramAllowanceWrapper.allowanceAvailableBeforeOrder, which is what checkout enforces.
  availableBalanceAmt: number | null;
}

/**
 * Access state for one Program, for one user. Exactly one of these describes a program row.
 * See calcProgramAccessState for the precedence between them, and
 * Docs/program-user-group-access.md §6.4 for the vocabulary and why each one is worded the
 * way it is.
 *
 * The important distinction: `Blocked` is ALWAYS a misconfiguration, while
 * `ExcludedByDesign` is a legitimate, deliberate setting. They must never be presented alike.
 */
export type ProgramAccessState =
  | 'CanOrder'
  | 'Blocked'
  | 'ExcludedByDesign'
  | 'GroupInactive'
  | 'ProgramInactive'
  | 'Expired'
  | 'NotStarted'
  | 'NotYetOpen';

/** One candidate Program User Group for a program — an option in the chooser. */
export interface PugAccessChoice {
  programUserGroupId: string;
  programUserGroupName: string;
  isPugActive: boolean;
  pugStatusName: string;
  isExcluded: boolean;
  /** PUG is Active AND the user is not excluded from it on this program. */
  isAllowed: boolean;
  /** One-line summary of what ordering through this group would mean. */
  detail: string;
}

/** One Program the user can reach through at least one of their PUGs. */
export interface ProgramAccessRow {
  programId: string;
  programName: string;
  programTypeLabel: string;
  choices: PugAccessChoice[];
  /** Governing PUG name, '—' when none, or 'N groups' when the invariant is violated. */
  routedThroughName: string;
  state: ProgramAccessState;
  statusLabel: string;
  canChoose: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProgramRulesService {
  public isAccountAdmin = false;

  constructor(
    public dbQueryService: AccountDbQueryService,
    authService: AuthService,
  ) {
    this.isAccountAdmin = authService.getUser()?.accountAdminId != null;
  }

  // Determine which programs are available for this user.
  // Takes into account activeStatus of Account, Program and User
  // as well as programUserGroups and exclusions and date ranges of programs.
  // Assumes accountUser.account has been fetched.
  public async findOpenProgramInfoItemsByAccountUser(au: AccountUser, existingJobOrders: JobOrder[], skipAllowances = false) {
    const puItems = await this.getProgramUserItemsWithAvailability(au, skipAllowances);
    const availablePuItems = puItems.filter((x) => x.err == null);

    const existingProgramIdSet = new Set(
      existingJobOrders
        .filter(
          (jo) =>
            jo.jobOrderStatusId == JobOrderStatusEnum.Setup ||
            jo.jobOrderStatusId == JobOrderStatusEnum.AwaitingApproval ||
            jo.jobOrderStatusId == JobOrderStatusEnum.Rejected ||
            jo.jobOrderStatusId == JobOrderStatusEnum.OnHold,
        )
        .map((jo) => jo.programId),
    );
    const openPuItems = availablePuItems.filter((puItem) => !existingProgramIdSet.has(puItem.program.id));
    // TODO: add filter for ApprovalTreeUserGroups
    return openPuItems;
  }

  private async getUnavailableProgramInfoItemsByAccountUser(au: AccountUser) {
    const puItems = await this.getProgramUserItemsWithAvailability(au);
    // Expired programs are treated as if they weren't attached — never listed
    // as blockers. The "no valid programs at all" case is handled by
    // getOrderIssuesForUser via its own hasNonExpiredProgram check.
    const nonExpired = puItems.filter((x) => !this.isExpiredProgram(x));
    // Per-Program short-circuit: buildProgramUserItemsExpanded emits one row per PUG membership,
    // including Inactive/consolidated/excluded ghosts (each with a per-row err explaining why THAT
    // row isn't governing). For Ordering Status we only care whether the user has ANY viable path
    // to each Program. If at least one row for a Program has no err (winner PUG is Active + user
    // included), the Program is available — drop every other row for that Program from the issue
    // list so old ghost errors don't hide the working path.
    const programsWithGoodRow = new Set(
      nonExpired.filter((x) => x.err == null).map((x) => x.program.id),
    );
    return nonExpired.filter((x) => x.err != null && !programsWithGoodRow.has(x.program.id));
  }

  public async getProgramUserItemsWithAvailability(au: AccountUser, skipAllowances = false) {
    UtilFns.assert(au.account != null, ' au.account must be fetched prior to running this method.');
    if (au.proximityUser.activeStatusId != ActiveStatusEnum.Active) {
      return [];
    }
    if (au.account.activeStatusId != ActiveStatusEnum.Active) {
      return [];
    }
    const puItems = await this.getProgramUserItems(au, skipAllowances);

    puItems.forEach((x) => {
      const { ok, msg } = this.isAvailableProgram(x, true, false);
      if (!ok) {
        x.err = msg;
      }
    });

    await this.applyOneOrderPerCollectionRule(au, puItems);
    return puItems;
  }

  /**
   * While a program is collecting, each Account User may place ONE order on it.
   *
   * The boundary is Program.CollectionStartDate — the day the current episode opened — and not
   * "any order on this program", which would reach back into previous cycles, nor
   * CollectionReleasedTs, which records when an episode CLOSED and is cleared when the next one
   * opens. See Docs/Collect-Organize-Place.md §4b.
   *
   * Programs not in COP mode are untouched: ordering there behaves exactly as it always has.
   */
  private async applyOneOrderPerCollectionRule(au: AccountUser, puItems: ProgramUserItem[]) {
    const collecting = puItems.filter(
      (x) => x.err == null && x.program.collectAndPlace && x.program.collectionStartDate != null,
    );
    if (collecting.length === 0) return;

    const results = await Promise.all(
      collecting.map((x) =>
        this.dbQueryService.getPlacedJobOrdersForUserAndProgramSince(au.id, x.program.id, x.program.collectionStartDate!),
      ),
    );

    collecting.forEach((x, i) => {
      if (results[i].length === 0) return;
      const when = DateFns.fmtDate(x.program.collectionStartDate!);
      x.err =
        `You have already placed an order for this collection. This program is collecting orders for ` +
        `administrator review, and one order per person is allowed for the collection that opened on ${when}. ` +
        `Contact your administrator if it needs to be changed.`;
    });
  }

  // Requires that Program.approvalTree.approvalTreeUserGroups has been fetched.
  public async getProgramUserItem(program: Program, accountUser: AccountUser) {
    const pugms = await this.dbQueryService.getProgramUserGroupMapsForUser(accountUser.id);
    return this.buildProgramUserItem(program, accountUser, pugms);
  }

  public async getProgramUserItems(accountUser: AccountUser, skipAllowances = false) {
    const pugms = await this.dbQueryService.getProgramUserGroupMapsForUser(accountUser.id);
    const progs = await this.dbQueryService.getProgramsForProgramUserGroups(
      pugms.map((x) => x.programUserGroup.id),
      skipAllowances,
    );

    // Build items for EACH PUG/Program combination (not just one per program)
    // This allows displaying all PUGs even when multiple PUGs point to the same program
    const puItems = this.buildProgramUserItemsExpanded(progs, accountUser, pugms);

    return puItems.filter((x) => x.programUserGroupMap != null || x.err != null);
  }

  /**
   * Builds ProgramUserItems for EACH PUG/Program combination.
   * When a user has multiple PUGs that point to the same program, this creates
   * one item per PUG (all marked with the "multiple PUGs" error).
   */
  private buildProgramUserItemsExpanded(programs: Program[], accountUser: AccountUser, pugms: ProgramUserGroupMap[]): ProgramUserItem[] {
    const items: ProgramUserItem[] = [];
    const accountUserId = accountUser.id;

    for (const program of programs) {
      const progAtugs = program.approvalTree.approvalTreeUserGroups;

      // Find all ATUGs where the user has membership
      const userAtugs = progAtugs.filter((atug) => pugms.find((pugm) => pugm.programUserGroupId === atug.programUserGroupId));

      // Determine which ATUGs are effectively "allowed" — i.e., capable of routing new orders.
      // A PUG is disallowed if it's Inactive OR if the user is per-program excluded from it.
      // Inactive covers both admin-deactivated PUGs and consolidated losers (whose membership
      // rows we deliberately leave in place so in-flight orders complete on the loser).
      const isAllowed = (atug: ApprovalTreeUserGroup) => {
        if (atug.programUserGroup.activeStatusId === ActiveStatusEnum.Inactive) return false;
        const exclusions = atug.programUserGroup.programUserGroupExclusions.filter(
          (x) => x.programId === program.id && x.accountUserId === accountUserId,
        );
        return exclusions.length === 0;
      };

      const allowedCount = userAtugs.filter(isAllowed).length;
      const hasMultiplePugViolation = allowedCount > 1;

      // Emit a row for EVERY membership so the ledger card shows the user's full PUG picture
      // for this Program (allowed, excluded, and inactive alike). Each row's err explains why
      // it isn't routing orders (if applicable).
      for (const atug of userAtugs) {
        const pugm = pugms.find((x) => x.programUserGroupId === atug.programUserGroupId);
        const pug = atug.programUserGroup;
        const excluded = pug.programUserGroupExclusions.some(
          (x) => x.programId === program.id && x.accountUserId === accountUserId,
        );

        // Precedence: exclusion > inactive > multiple-PUG violation > no error.
        // Hold and Phaseout are NOT issues — Hold is a temporary admin state (e.g. mid-edit
        // or mid-consolidation) and the row still routes orders once the account comes back
        // off Hold. The status column colors them orange; no need to duplicate in Issue.
        let err: string | null = null;
        if (excluded) {
          err = 'This user has been excluded from this program';
        } else if (pug.activeStatusId === ActiveStatusEnum.Inactive) {
          err = pug.consolidatedIntoPugId
            ? `The program user group "${pug.name}" was consolidated into another PUG and is inactive.`
            : `The program user group "${pug.name}" is not active.`;
        } else if (hasMultiplePugViolation) {
          err = 'Multiple user Program User Groups point to this Program. When that happens, only one (1) Program User Group can be INCLUDED and the rest must be EXCLUDED.';
        }

        items.push({
          program,
          accountUser,
          approvalTreeUserGroup: atug,
          programUserGroupMap: pugm,
          err,
        } as ProgramUserItem);
      }
    }

    return items;
  }

  // requires program.approvalTree.approvalTreeUserGroups to be fetched.
  public buildProgramUserItem(program: Program, accountUser: AccountUser, pugms: ProgramUserGroupMap[]) {
    const accountUserId = accountUser.id;
    const progAtugs = program.approvalTree.approvalTreeUserGroups;
    // const progPugs = program.approvalTree.approvalTreeUserGroups.map((x) => x.programUserGroup);
    // restrict pugs to just those for the current user.
    const atugs = progAtugs.filter((x) => pugms.find((y) => y.programUserGroup.id == x.programUserGroup.id));
    // const pugs = progPugs.filter((x) => pugms.find((y) => y.programUserGroup.id == x.id));
    //include prog is pugs.length > 0
    const allowedAtugs = atugs.filter((atug) => {
      // Inactive PUGs are effectively excluded — see buildProgramUserItemsExpanded comment.
      if (atug.programUserGroup.activeStatusId === ActiveStatusEnum.Inactive) return false;
      const exclusions = atug.programUserGroup.programUserGroupExclusions.filter(
        (x) => x.programId == program.id && x.accountUserId == accountUserId,
      );
      return exclusions.length == 0;
    });

    let err: string | null = null;

    if (allowedAtugs.length == 0) {
      err = `This user has been excluded from this program</br></br>`;
    }

    const allowedPugms = allowedAtugs.map((atug) => pugms.find((x) => x.programUserGroupId == atug.programUserGroupId));
    if (allowedPugms.length > 1) {
      const dupPugNames = allowedPugms.map((pugm) => pugm?.programUserGroup.name).join('</br>');
      // err =
      //   `A maximum of one program user group is allowed per program/user - This user belongs to the following program user groups ( after exclusions):</br></br>` +
      //   dupPugNames;
      err =
        'Multiple user Program User Groups point to this Program.  When that happens, only one (1) Program User Group can be INCLUDED and the rest must be EXCLUDED.';
    }

    if (allowedPugms.length == 1 && allowedPugms[0]?.programUserGroup.activeStatusId != ActiveStatusEnum.Active) {
      err = `The program user group "${allowedPugms[0]?.programUserGroup.name}" is not active.`;
    }

    // const atug = _.first(allowedAtugs);
    // if (program.programBillingTypeId == ProgramBillingTypeEnum.ProgramUserGroupBillingAddress) {
    //   if (!atug?.billingAccountAddressId) {
    //     err =
    //       'This program is setup to use a Program User Group Billing Address, but no billing address has been assigned to your program user group for this program.';
    //   }
    //
    return {
      program: program,
      accountUser: accountUser,
      approvalTreeUserGroup: _.first(allowedAtugs),
      programUserGroupMap: _.first(allowedPugms),

      err,
    } as ProgramUserItem;
  }

  public isAvailableProgram(puItem: ProgramUserItem, allowPuError: boolean, isNewOrder: boolean): { ok: boolean; msg: string | null } {
    let msg!: string | null;
    let ok = false;
    if (puItem.err) {
      if (!allowPuError) msg = puItem.err;
    } else if (this.isExpiredProgram(puItem)) {
      msg = 'Proximity Program has expired';
    } else if (this.isFutureProgram(puItem)) {
      msg = 'Proximity Program has not started';
      // Next two are account status validations
    } else if (isNewOrder && puItem.program.account.activeStatusId != ActiveStatusEnum.Active) {
      msg = `Proximity Account is not active - current status is ${puItem.program.account.activeStatus?.name}`;
    } else if (
      !isNewOrder &&
      puItem.program.account.activeStatusId != ActiveStatusEnum.Active &&
      puItem.program.account.activeStatusId != ActiveStatusEnum.Phaseout
    ) {
      // msg = `Proximity Account is not active or in phaseout - current status is ${puItem.program.account.activeStatus?.name}`;
      msg = `Proximity Account is not active or in phaseout - current status is ${puItem.program.account.activeStatus?.name}`;
      // Next is user status validation
    } else if (puItem.accountUser.proximityUser.activeStatusId != ActiveStatusEnum.Active) {
      msg = `Proximity User is not active - current status is ${puItem.accountUser.proximityUser.activeStatus?.name}`;
      // Next two are program status validations
    } else if (isNewOrder && puItem.program.activeStatusId != ActiveStatusEnum.Active) {
      msg = `Proximity Program is not active - current status is ${puItem.program.activeStatus?.name}`;
    } else if (
      !isNewOrder &&
      puItem.program.activeStatusId != ActiveStatusEnum.Active &&
      puItem.program.activeStatusId != ActiveStatusEnum.Phaseout
    ) {
      // msg = `Proximity Program is not active or in phaseout - current status is ${puItem.program.activeStatus?.name}`;
      msg = `Proximity Program current status is ${puItem.program.activeStatus?.name}`;
    } else {
      const now = new Date().getTime();

      const minBeginDate = DateFns.dateAdd(puItem.programUserGroupMap.joinDate, puItem.program.availableAfterJoinDateDays - 1, 'days'); // yesterday
      if (minBeginDate.getTime() > now) {
        msg =
          'Proximity Program will be available to you to place orders on ' +
          DateFns.dateAdd(puItem.programUserGroupMap.joinDate, puItem.program.availableAfterJoinDateDays, 'days');
      }

      if (
        isNewOrder &&
        puItem.program.startDate != null &&
        puItem.approvalTreeUserGroup.orderTimeFrameStartDays != null &&
        puItem.approvalTreeUserGroup.orderTimeFrameDurationDays != null
      ) {
        const frameStartDate = DateFns.dateAdd(puItem.program.startDate, puItem.approvalTreeUserGroup.orderTimeFrameStartDays, 'days');
        const frameEndDate = DateFns.dateAdd(frameStartDate, puItem.approvalTreeUserGroup.orderTimeFrameDurationDays, 'days');
        if (frameStartDate.getTime() > now || frameEndDate.getTime() < now) {
          msg = `Proximity Program is only available to you to place orders between ${DateFns.fmtDate(frameStartDate)} and ${DateFns.fmtDate(frameEndDate)}`;

          if (this.isAccountAdmin) {
            ok = true; // Admins can place orders outside of the time frame
          }
        }
      }
    }

    if (!msg) {
      ok = true;
    }
    return { ok, msg };
  }

  /**
   * Determines if a program is expired for a specific user based on the program's start date type.
   * - FixedStartAndEndDate (0) and VariableStartFixedEndDate (2): Uses program.endDate
   * - VariableStartAndEndDate (1): Calculates end date from joinDate + durationDays
   */
  public isProgramExpiredForUser(program: Program, joinDate: Date | null | undefined): boolean {
    const endDate = this.getProgramEndDateForUser(program, joinDate);
    if (endDate == null) return false;
    return new Date().getTime() >= endDate.getTime();
  }

  /**
   * The date this program stops accepting orders from THIS user, or null when it never expires
   * (Inventory) or no end date can be determined - a variable-duration program with no
   * durationDays, or a fixed-end program whose endDate was never set.
   *
   * Single source of truth for expiry: isProgramExpiredForUser compares it against now, and
   * getOrderIssuesForUser reports it back to the user. Null rather than a throw on the
   * incomplete-configuration cases, so a half-configured program cannot blow up the ledger card;
   * it reads as "not expired" and its real problem surfaces through its own error.
   */
  public getProgramEndDateForUser(program: Program, joinDate: Date | null | undefined): Date | null {
    if (program.programTypeId === ProgramTypeEnum.Inventory) {
      return null; // Inventory programs do not expire
    }
    if (program.hasExplicitEndDate()) {
      return program.endDate ?? null;
    }
    if (joinDate == null || program.durationDays == null) {
      return null;
    }
    return DateFns.dateAdd(joinDate, program.durationDays, 'days');
  }

  // ---------------------------------------------------------------------------------------
  // Collect, Organize and Place
  // ---------------------------------------------------------------------------------------

  /**
   * Gate #1: the earliest date on which a collected batch may be placed with suppliers.
   * Returns null when no gate date can be determined.
   *
   * Program Wide  — the program's own EarliestPlacementDate.
   * PUG Wide      — the end of that PUG's ordering window, i.e.
   *                 program.startDate + orderTimeFrameStartDays + orderTimeFrameDurationDays.
   *                 Same computation as the ordering-window check in isAvailableProgram().
   *
   * The PUG Wide inputs are nullable and a program is supposed to be blocked from activating in
   * that scope until every ATUG has them set. This returns null rather than guessing if they are
   * missing — see shouldCollectOrder() for what null means.
   */
  public getEarliestPlacementDate(program: Program, atug?: ApprovalTreeUserGroup): Date | null {
    if (program.collectionScopeId === CollectionScopeEnum.ProgramWide) {
      return program.earliestPlacementDate ?? null;
    }

    if (program.startDate == null || atug?.orderTimeFrameStartDays == null || atug?.orderTimeFrameDurationDays == null) {
      return null;
    }
    const frameStart = DateFns.dateAdd(program.startDate, atug.orderTimeFrameStartDays, 'days');
    return DateFns.dateAdd(frameStart, atug.orderTimeFrameDurationDays, 'days');
  }

  /**
   * True when a newly-approved order on this program should be COLLECTED (AwaitingRelease) rather
   * than dispatched to the supplier (AwaitingPlacement).
   *
   * NOTE THE PLACEMENT DATE PLAYS NO PART IN THIS. The date is soft: orders keep collecting after
   * it passes, because late submissions are expected and a straggler arriving on release day should
   * join the batch rather than race ahead of it. What ends collection is the RELEASE, recorded as
   * CollectionReleasedTs on the scope holder:
   *
   *   not collecting                        -> false, normal flow
   *   collecting, not yet released          -> true,  collected (whatever the date says)
   *   collecting, batch already released    -> false, normal flow
   *   collecting, next cycle opened         -> true,  collected again (release ts cleared)
   *
   * The date's only job is to gate the release action — releasing before it warns, since that is
   * the date the organization was told about — and to drive the nag once it lapses.
   *
   * Program Wide reads the flag off the Program; PUG Wide reads it off the ApprovalTreeUserGroup,
   * so each group's batch releases independently.
   */
  public shouldCollectOrder(program: Program, atug?: ApprovalTreeUserGroup): boolean {
    return ProgramRulesService.isCollecting(program, atug);
  }

  /** Static form of shouldCollectOrder, for the static validators in JobOrderRulesService. */
  public static isCollecting(program: Program, atug?: ApprovalTreeUserGroup): boolean {
    if (!program.collectAndPlace) return false;

    if (program.collectionScopeId === CollectionScopeEnum.ProgramWide) {
      return program.collectionReleasedTs == null;
    }

    // PUG Wide with no ATUG means the group is no longer attached to the program's tree — an order
    // cannot legitimately be in that state (validatePurchaseOrder asserts on it). Fall through to
    // normal dispatch rather than collecting against a batch that has no owner and no release path.
    if (atug == null) return false;
    return atug.collectionReleasedTs == null;
  }

  private isExpiredProgram(puItem: ProgramUserItem) {
    if (puItem.program.programTypeId === ProgramTypeEnum.Inventory) {
      return false; // Inventory programs do not expire
    }
    return this.isProgramExpiredForUser(puItem.program, puItem.programUserGroupMap?.joinDate);
  }

  private isFutureProgram(puItem: ProgramUserItem) {
    const now = new Date().getTime();

    if (puItem.program.programTypeId === ProgramTypeEnum.Inventory) {
      return false; // Inventory programs are never future programs
    }

    // Check if program has an explicit start date that is in the future
    if (puItem.program.startDate != null) {
      return puItem.program.startDate.getTime() > now;
    }
    return false;
  }

  public async findOpenProgramAllowancesByAccountUser(au: AccountUser, openPrograms: Program[]) {
    const openAllowances: ProgramAllowance[] = [];
    const userProgramUserGroups = au.programUserGroupMaps.map((p) => p.programUserGroup);
    openPrograms.forEach((pro) => {
      pro.programAllowances.forEach((pa) => {
        pa.programAllowanceUserGroupMaps.forEach((map) => {
          if (userProgramUserGroups.includes(map.programUserGroup)) {
            openAllowances.push(pa);
          }
        });
      });
    });
    return openAllowances;
  }

  //#region Program Type Detection Methods

  /**
   * Determines if a program is an "Issuance Program" for a given user's Program User Group.
   * A program is an Issuance Program if the user's PUG has access to either:
   * (a) ProgramIssuance entries via programIssuanceUserGroupMaps, OR
   * (b) AccountIssuance entries via accountIssuanceUserGroupMaps
   *
   * @param program The program to check (must have programIssuances.programIssuanceUserGroupMaps expanded)
   * @param programUserGroupId The user's Program User Group ID
   * @param accountIssuances Account issuances that apply to the user's PUG (from getAccountIssuancesForProgramUserGroups)
   * @returns true if this is an Issuance Program for the user
   */
  public isIssuanceProgramForUser(program: Program, programUserGroupId: string, accountIssuances: AccountIssuance[]): boolean {
    // Check Program-level issuances
    const hasProgramIssuance = program.programIssuances.some((pi) =>
      pi.programIssuanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId),
    );

    if (hasProgramIssuance) {
      return true;
    }

    // Check Account-level issuances
    const hasAccountIssuance = accountIssuances.some((ai) =>
      ai.accountIssuanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId),
    );

    return hasAccountIssuance;
  }

  /**
   * Determines if a program is an "Allowance Program" for a given user's Program User Group.
   * A program is an Allowance Program if it has ProgramAllowance entries where
   * programAllowanceUserGroupMaps includes the user's PUG.
   *
   * @param program The program to check (must have programAllowances.programAllowanceUserGroupMaps expanded)
   * @param programUserGroupId The user's Program User Group ID
   * @returns true if this is an Allowance Program for the user
   */
  public isAllowanceProgramForUser(program: Program, programUserGroupId: string): boolean {
    return program.programAllowances.some((pa) =>
      pa.programAllowanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId),
    );
  }

  /**
   * Gets the ProgramAllowance for a given user's Program User Group, if one exists.
   *
   * @param program The program to check (must have programAllowances.programAllowanceUserGroupMaps expanded)
   * @param programUserGroupId The user's Program User Group ID
   * @returns The ProgramAllowance or null if not found
   */
  public getAllowanceForUserGroup(program: Program, programUserGroupId: string): ProgramAllowance | null {
    return (
      program.programAllowances.find((pa) =>
        pa.programAllowanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId),
      ) ?? null
    );
  }

  //#endregion

  //#region Order Issue Detection

  /**
   * Collects all issues preventing an account user from placing an order.
   * Returns an array of OrderIssue objects with messages and optional details.
   *
   * @param accountUser The account user to check (can be null/undefined)
   * @param account The account the user belongs to
   * @returns Array of OrderIssue objects describing why orders cannot be placed
   */
  public async getOrderIssuesForUser(accountUser: AccountUser | undefined, account: Account): Promise<OrderIssue[]> {
    const issues: OrderIssue[] = [];

    if (accountUser == null) {
      issues.push({ message: 'Unknown user.' });
      return issues;
    }

    const accountIsActive = account.activeStatusId == ActiveStatusEnum.Active;
    const userIsActive = accountUser.proximityUser.activeStatusId == ActiveStatusEnum.Active;

    if (!accountIsActive) {
      issues.push({ message: `${account.name} is not active in Proximity and cannot accept orders.` });
    }

    if (!userIsActive) {
      issues.push({ message: 'Your Proximity User Account is not active and cannot place orders.' });
    }

    const pugms = await this.dbQueryService.getProgramUserGroupMapsForUser(accountUser.id);

    // Determine whether the user is attached to any NON-EXPIRED program. An
    // expired program is treated as if it weren't attached at all, so a user
    // whose only PUG memberships route to expired programs is considered to
    // have no assignments for order-blocking purposes.
    // NOTE: getProgramUserItemsWithAvailability short-circuits to [] when the
    // Account or User is not Active, so hasNonExpiredProgram would be false in
    // those cases regardless of actual PUG membership. Gate the derived
    // "no PUGs" message on accountIsActive && userIsActive so we don't pile a
    // misleading symptom on top of the real, already-reported root cause.
    const puItems = await this.getProgramUserItemsWithAvailability(accountUser);
    const hasNonExpiredProgram = puItems.some((x) => !this.isExpiredProgram(x));

    if (pugms.length == 0) {
      issues.push({ message: 'You have no assigned Proximity Program User Groups.' });
    } else if (accountIsActive && userIsActive && !hasNonExpiredProgram) {
      // The user HAS groups; every Program those groups reach has expired. This case used to share
      // the "no assigned Program User Groups" message above, which reads as a membership problem
      // and sends admins hunting through the PUG screens for a break that isn't there - while the
      // ledger card simultaneously listed those same groups as Active. Say what actually happened.
      issues.push(this.buildAllProgramsExpiredIssue(accountUser, puItems));
    } else {
      // Order-placement diagnostic: Hold is a real blocker here even though it's not surfaced
      // as an "issue" in the ledger card (where Hold is a transient admin state). For each
      // Program, if the user has NO Active-PUG path AND no other error blocks the row, add an
      // explicit Hold-path issue. Prevents the silent "Contact Administrator" fallback when
      // the only paths to the Program are on Hold PUGs.
      const firstName = accountUser.proximityUser.firstName || accountUser.proximityUser.fullName;
      const byProgram = new Map<string, ProgramUserItem[]>();
      for (const item of puItems) {
        if (this.isExpiredProgram(item)) continue;
        const arr = byProgram.get(item.program.id) ?? [];
        arr.push(item);
        byProgram.set(item.program.id, arr);
      }
      for (const [, rows] of byProgram) {
        const anyActivePath = rows.some(
          (r) => r.err == null && r.programUserGroupMap?.programUserGroup?.activeStatusId === ActiveStatusEnum.Active,
        );
        if (anyActivePath) continue;
        // No Active path, but at least one Hold/Phaseout path exists → surface the Hold reason.
        const holdRow = rows.find(
          (r) => r.err == null && r.programUserGroupMap?.programUserGroup?.activeStatusId !== ActiveStatusEnum.Active,
        );
        if (holdRow) {
          const pug = holdRow.programUserGroupMap!.programUserGroup;
          issues.push({
            message: `<strong>${holdRow.program.name}:</strong> the Program User Group "${pug.name}" is on ${pug.activeStatus?.name ?? 'Hold'}. ${firstName} cannot place orders until the group is reactivated.`,
            programId: holdRow.program.id,
          });
        }
      }

      const unavailableItems = await this.getUnavailableProgramInfoItemsByAccountUser(accountUser);
      for (const item of unavailableItems) {
        // Parse the error message to extract program user group names if present
        const errText = item.err || '';
        let mainMessage = errText;
        let details: string[] = [];

        // Check if error contains program user group names (separated by </br>)
        if (errText.includes('</br></br>')) {
          const parts = errText.split('</br></br>');
          mainMessage = parts[0];
          if (parts[1]) {
            details = parts[1].split('</br>').filter((d) => d.trim());
          }
        }

        issues.push({
          message: `<strong>${item.program.name}:</strong> ${mainMessage}`,
          details: details.length > 0 ? details : undefined,
          programId: item.program.id,
        });
      }
    }

    return issues;
  }

  /**
   * The user belongs to Program User Groups, but no Program those groups reach is still live for
   * them. Every puItem is expired here - the caller only invokes this when hasNonExpiredProgram
   * is false.
   *
   * Deliberately account-level (no programId): it is a statement about the user's whole program
   * picture rather than about one program, and the ledger card's Ordering Status only renders
   * issues that carry no programId (see account-user-summary.service.ts, accountLevelBlockers).
   */
  private buildAllProgramsExpiredIssue(accountUser: AccountUser, puItems: ProgramUserItem[]): OrderIssue {
    const firstName = accountUser.proximityUser.firstName || accountUser.proximityUser.fullName;

    // One entry per Program - reaching the same Program through two groups must not read as two
    // expired programs.
    const labelByProgramId = new Map<string, string>();
    for (const item of puItems) {
      if (labelByProgramId.has(item.program.id)) continue;
      const endDate = this.getProgramEndDateForUser(item.program, item.programUserGroupMap?.joinDate);
      labelByProgramId.set(item.program.id, endDate == null ? item.program.name : `${item.program.name} (expired ${DateFns.fmtDate(endDate)})`);
    }

    if (labelByProgramId.size == 0) {
      // Groups that reach no Program at all - a different failure reaching the same dead end.
      return {
        message: `${firstName} is assigned to Program User Groups, but none of them are attached to a Proximity Program.`,
      };
    }

    const labels = Array.from(labelByProgramId.values()).sort((a, b) => a.localeCompare(b));
    const lead = labels.length == 1 ? 'The only Proximity Program' : 'Every Proximity Program';
    return {
      message:
        `${lead} assigned to ${firstName} has expired: <strong>${labels.join('</strong>, <strong>')}</strong>. ` +
        `${firstName} cannot place orders until a current program is assigned.`,
    };
  }

  //#endregion

  //#region Program Access Items

  /**
   * Gets all program access items for a given account user.
   * This includes all programs the user has access to via their Program User Groups,
   * along with detailed information about issuances, allowances, and exclusions.
   *
   * @param accountUser The account user to get program access for
   * @returns Array of ProgramAccessItem objects with full program details
   */
  public async getProgramAccessItemsForUser(accountUser: AccountUser): Promise<ProgramAccessItem[]> {
    // Get program user group maps for this user
    const pugms = await this.dbQueryService.getProgramUserGroupMapsForUser(accountUser.id);

    if (pugms.length === 0) {
      return [];
    }

    // Get programs for these program user groups. Read the FK, not the navigation property:
    // the FK is a column on the row and is always there, while the navigation resolves through
    // the Breeze cache and goes null the moment the entity is detached - see the guard below.
    const programUserGroupIds = pugms.map((pugm) => pugm.programUserGroupId);
    const accountId = accountUser.accountId;

    // THESE THREE GO TOGETHER ON PURPOSE. Each needs only the user and their group ids, which are
    // already in hand, so running them one after another spent three round trips to learn nothing
    // the first one did not already allow. Only the allowance LOGS genuinely depend on an earlier
    // answer - they are keyed by programId - so they are the only thing left waiting below.
    const [programs, accountIssuances, userAllowanceRemainingAmt] = await Promise.all([
      this.dbQueryService.getProgramsForProgramUserGroups(programUserGroupIds),
      this.dbQueryService.getAccountIssuancesForProgramUserGroups(accountId, programUserGroupIds),
      this.dbQueryService.getAccountUserAllowanceRemaining(accountUser.id),
    ]);

    // Allowance logs for this user across all programs. The global (cross-program) pot above
    // spends alongside every program allowance.
    const programIds = programs.map((p) => p.id);
    const allowanceLogs = await this.dbQueryService.getProgramAllowanceUserLogsForUserByPrograms(
      accountUser.id,
      programIds,
    );

    // Everything above is async, and the page that asked for this can be gone by the time it
    // finishes. QueryComponent.deactivate() detaches every registered entity type on the way
    // out, which nulls the navigation properties the items below are built from. That is what
    // crashed this method when a user left Place an Order mid-load. Nothing wants a
    // half-resolved answer for a page that no longer exists, so stop here rather than build one.
    // The account user is checked too: the caller loaded it BEFORE this method started, so a
    // teardown during the caller's own awaits detaches it (and nulls accountUser.account) while
    // the maps queried above come back fresh and attached. Checking only the maps missed that
    // window, and buildPlaceOrderCardItems then read accountUser.account.activeStatusId on null.
    if (accountUser.entityAspect.entityState.isDetached() || pugms.some((pugm) => pugm.programUserGroup == null)) {
      return [];
    }

    // Build the access items with all the new information
    return this.buildProgramAccessItems(accountUser, programs, pugms, accountIssuances, allowanceLogs, userAllowanceRemainingAmt);
  }

  /**
   * Money left in the user's global (cross-program) allowance pot.
   *
   * ⚠️ `userAllowanceRemainingAmt` is SUM(AccountUserAllowanceLog.Amt) and in that ledger credits
   * are NEGATIVE, so a user with $55 available reads as -55. Negating it gives the spendable
   * amount. A positive result here is credit; a negative result means the pot is overdrawn and
   * correctly reduces what the program can spend. Same convention as
   * ProgramAllowanceWrapper.curUserAllowanceUsedAmt — do not "fix" the sign in either place
   * without the other.
   */
  private globalAllowanceAvailableAmt(userAllowanceRemainingAmt: number): number {
    return -(userAllowanceRemainingAmt ?? 0);
  }

  /**
   * Builds ProgramAccessItem objects from raw program and user data.
   */
  private buildProgramAccessItems(
    accountUser: AccountUser,
    programs: Program[],
    pugms: ProgramUserGroupMap[],
    accountIssuances: AccountIssuance[],
    allowanceLogs: ProgramAllowanceUserLog[],
    userAllowanceRemainingAmt: number,
  ): ProgramAccessItem[] {
    const items: ProgramAccessItem[] = [];
    const globalAllowanceAmt = this.globalAllowanceAvailableAmt(userAllowanceRemainingAmt);

    for (const program of programs) {
      // Find which program user groups give access to this program
      const programAtugs = program.approvalTree?.approvalTreeUserGroups || [];

      for (const atug of programAtugs) {
        // Check if this user has this program user group
        const userPugm = pugms.find((pugm) => pugm.programUserGroupId === atug.programUserGroupId);

        if (userPugm) {
          const programUserGroupId = userPugm.programUserGroupId;

          // Check if the user is excluded from this program/group combination
          const isExcluded = (program.programUserGroupExclusions || []).some(
            (exc) => exc.accountUserId === accountUser.id && exc.programUserGroupId === programUserGroupId,
          );

          // Check if the program is expired for this user (handles all ProgramStartDateTypes)
          const isExpired = this.isProgramExpiredForUser(program, userPugm.joinDate);

          // Determine display status
          const displayStatus = isExpired ? 'Expired' : program.activeStatus?.name || '';

          // Determine if this is an Issuance Program for the user
          const isIssuanceProgram = this.isIssuanceProgramForUser(program, programUserGroupId, accountIssuances);

          // Determine if this is an Allowance Program for the user
          const isAllowanceProgram = this.isAllowanceProgramForUser(program, programUserGroupId);

          const isInventoryProgram = program.programTypeId === ProgramTypeEnum.Inventory;

          // Determine program type
          const programType = this.getProgramTypeLabel(isIssuanceProgram, isAllowanceProgram, isInventoryProgram);

          // Get admin names for this program user group from the approval tree
          const adminNames = this.getAdminNamesForUserGroup(atug);

          // Get issuance categories that apply to this user group
          const issuanceCategories = this.getIssuanceCategoriesForUserGroup(program, programUserGroupId);

          // Get issuance details with min/max quantities
          const issuanceDetails = this.getIssuanceDetailsForUserGroup(program, programUserGroupId);

          // Get account issuance details that apply to this user group
          const accountIssuanceDetails = this.getAccountIssuanceDetailsForUserGroup(program, programUserGroupId, accountIssuances);

          // Get allowance names that apply to this user group
          const allowanceNames = this.getAllowanceNamesForUserGroup(program, programUserGroupId);

          // Calculate allowance balances if this is an allowance program
          let allowanceAmt: number | null = null;
          let chargedAmt: number | null = null;
          let adjustmentsAmt: number | null = null;
          let programBalanceAmt: number | null = null;
          let availableBalanceAmt: number | null = null;

          if (isAllowanceProgram) {
            const programAllowance = this.getAllowanceForUserGroup(program, programUserGroupId);
            if (programAllowance) {
              allowanceAmt = programAllowance.allowanceAmt;

              // Calculate charged and adjustments from logs for this allowance
              const logsForAllowance = allowanceLogs.filter((log) => log.programAllowanceId === programAllowance.id);
              const { charged, adjustments } = this.calculateAllowanceAmounts(logsForAllowance);
              chargedAmt = charged;
              adjustmentsAmt = adjustments;

              // Program-only balance = Allowance - Charged + Adjustments
              programBalanceAmt = allowanceAmt - chargedAmt + adjustmentsAmt;

              // The global pot spends on top of the program allowance, so what the user can
              // actually spend here is the two together — the number checkout enforces. The pot is
              // ONE shared balance across every allowance program, so a UI must not add it into
              // each program's own figures; state it once (see the place-order cards).
              availableBalanceAmt = programBalanceAmt + globalAllowanceAmt;
            }
          }

          items.push({
            program,
            programUserGroupId,
            programUserGroupName: userPugm.programUserGroup.name,
            programUserGroupStatus: userPugm.programUserGroup.activeStatus?.name || '',
            programUserGroupActiveStatusId: userPugm.programUserGroup.activeStatusId,
            joinDate: userPugm.joinDate || null,
            isExcluded,
            isExpired,
            displayStatus,
            isIssuanceProgram,
            isAllowanceProgram,
            programType,
            adminNames,
            issuanceCategories,
            issuanceDetails,
            accountIssuanceDetails,
            allowanceNames,
            allowanceAmt,
            chargedAmt,
            adjustmentsAmt,
            programBalanceAmt,
            globalAllowanceAmt,
            availableBalanceAmt,
          });
        }
      }
    }

    return items;
  }

  /**
   * What governs this user's ordering on a program THROUGH A PARTICULAR GROUP — a dollar
   * allowance, quantity issuances, both, or inventory.
   *
   * "Not Set" is a configuration gap, not a kind of program. A group with neither an allowance
   * nor issuances covering the program's product tags will stop that program from activating,
   * so this label means "this pairing is incomplete", not "this program has no limits". It
   * shows up when a user is routed through a group the program's allowance was never mapped to.
   */
  private getProgramTypeLabel(isIssuance: boolean, isAllowance: boolean, isInventory): string {
    if (isIssuance && isAllowance) return 'Issuance & Allowance';
    if (isIssuance) return 'Issuance';
    if (isAllowance) return 'Allowance';
    if (isInventory) return 'Inventory';
    return 'Not Set';
  }

  private getAdminNamesForUserGroup(atug: ApprovalTreeUserGroup): string[] {
    const adminGroup = atug.approvalTreeAdminGroup?.accountAdminGroup;
    if (!adminGroup?.accountAdminGroupMaps) return [];

    return adminGroup.accountAdminGroupMaps
      .map((map) => map.accountAdmin?.proximityUser?.fullName || map.accountAdmin?.proximityUser?.name)
      .filter((name): name is string => !!name);
  }

  private getIssuanceCategoriesForUserGroup(program: Program, programUserGroupId: string): string[] {
    const issuances = program.programIssuances || [];
    return issuances
      .filter((pi) => pi.programIssuanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId))
      .map((pi) => pi.programProductTypeTag?.name || pi.name)
      .filter((name) => !!name);
  }

  private getIssuanceDetailsForUserGroup(program: Program, programUserGroupId: string): IssuanceDetail[] {
    const issuances = program.programIssuances || [];
    return issuances
      .filter((pi) => pi.programIssuanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId))
      .map((pi) => ({
        name: pi.programProductTypeTag?.name || pi.name,
        minQty: pi.minimumQty ?? null,
        maxQty: pi.maximumQty ?? null,
      }));
  }

  private getAccountIssuanceDetailsForUserGroup(
    program: Program,
    programUserGroupId: string,
    accountIssuances: AccountIssuance[],
  ): AccountIssuanceDetail[] {
    // Filter account issuances that apply to this program and user group
    return accountIssuances
      .filter((ai) => {
        // Check if this account issuance is mapped to this program
        const isMappedToProgram = ai.programAccountIssuanceMaps?.some((map) => map.programId === program.id);
        // Check if this account issuance applies to this user group
        const isMappedToUserGroup = ai.accountIssuanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId);
        return isMappedToProgram && isMappedToUserGroup;
      })
      .map((ai) => ({
        name: ai.programProductTypeTag?.name || ai.name,
        timeframeDays: ai.timeframeDays,
        maxQty: ai.maximumQty,
      }));
  }

  private getAllowanceNamesForUserGroup(program: Program, programUserGroupId: string): string[] {
    const allowances = program.programAllowances || [];
    return allowances
      .filter((pa) => pa.programAllowanceUserGroupMaps?.some((map) => map.programUserGroupId === programUserGroupId))
      .map((pa) => pa.name)
      .filter((name) => !!name);
  }

  /**
   * Calculate charged and adjustment amounts from allowance logs.
   *
   * Sign Convention (from ProgramAllowanceUserLog - aligned with existing code):
   * - Positive amt = charge against allowance (reduces available balance)
   * - Negative amt = adjustment/credit (increases available balance)
   *
   * This follows the same pattern used in:
   * - ProgramAllowanceWrapper.prevProgramAllowanceUsedAmt = _.sum(logs.map(x => x.amt))
   * - AccountUserSummary.getAllowanceWrappers() creditAmt/debitAmt logic
   * - allowance-utilization-widget.component.ts usage calculation
   *
   * For this UI:
   * - Charged = sum of positive amounts (order charges)
   * - Adjustments = sum of negative amounts, negated for display (credits/adjustments that increase balance)
   */
  private calculateAllowanceAmounts(logs: ProgramAllowanceUserLog[]): { charged: number; adjustments: number } {
    let charged = 0;
    let adjustments = 0;

    for (const log of logs) {
      if (log.amt > 0) {
        charged += log.amt;
      } else {
        // Negative amt = credit/adjustment that increases balance
        // Negate for display so positive number means "added to balance"
        adjustments += -log.amt;
      }
    }

    return { charged, adjustments };
  }

  /**
   * Builds PlaceOrderCardItem[] from ProgramAccessItems for the program selection UI.
   * Shared by both the account-user card view and the admin program selection dialog.
   * Availability is determined by the account user's relationship to each program,
   * regardless of who is logged in.
   */
  public buildPlaceOrderCardItems(
    accessItems: ProgramAccessItem[],
    accountUser: AccountUser,
    existingJobOrders: JobOrder[],
  ): PlaceOrderCardItem[] {
    // No account means the account user was detached by a page teardown (see the guard in
    // getProgramAccessItemsForUser). The page is gone, so there is nothing to build.
    if (accountUser.account == null) {
      return [];
    }

    const items = accessItems.map((item) => {
      const isProgramActive = item.program.activeStatusId === ActiveStatusEnum.Active;
      const isAccountActive = accountUser.account.activeStatusId === ActiveStatusEnum.Active;
      const isPugActive = item.programUserGroupActiveStatusId === ActiveStatusEnum.Active;
      const isAvailable = !item.isExcluded && !item.isExpired && isProgramActive && isAccountActive && isPugActive;

      const existingJo =
        existingJobOrders.find(
          (jo) =>
            jo.programId === item.program.id &&
            (jo.jobOrderStatusId === JobOrderStatusEnum.Setup ||
              jo.jobOrderStatusId === JobOrderStatusEnum.AwaitingApproval ||
              jo.jobOrderStatusId === JobOrderStatusEnum.OnHold),
        ) || null;

      let statusLabel = item.displayStatus;
      if (item.isExcluded) statusLabel = 'Excluded';
      else if (item.isExpired) statusLabel = 'Expired';
      else if (!isProgramActive) statusLabel = `Program ${item.program.activeStatus?.name ?? 'Inactive'}`;
      else if (!isAccountActive) statusLabel = `Account ${accountUser.account.activeStatus?.name ?? 'Inactive'}`;
      else if (!isPugActive) statusLabel = 'Group Inactive';

      let buttonLabel: string;
      let buttonIcon: string;
      if (!isAvailable) {
        buttonLabel = 'Not Available';
        buttonIcon = 'pi pi-ban';
      } else if (existingJo) {
        buttonLabel = 'Resume Order';
        buttonIcon = 'pi pi-arrow-right';
      } else {
        buttonLabel = 'Place Order';
        buttonIcon = 'pi pi-shopping-cart';
      }

      return { accessItem: item, isAvailable, existingJo, statusLabel, buttonLabel, buttonIcon };
    });

    // Sort: available first, then unavailable
    items.sort((a, b) => (a.isAvailable === b.isAvailable ? 0 : a.isAvailable ? -1 : 1));
    return items;
  }

  //#endregion

  /*   public async placedCurrentAllowanceByAccountUserAmt (jobOrdersByAccountUser: JobOrder[] | null, openProgramsByAccountUser: Program[] | null, accountUser: AccountUser) {
    let amt = 0.00;
    if (!jobOrdersByAccountUser || !openProgramsByAccountUser) {
      openProgramsByAccountUser = await this.findOpenProgramsByAccountUser(accountUser, []);
      if (openProgramsByAccountUser.length == 0) {
        jobOrdersByAccountUser = await this.dbQueryService.getJobOrdersByAccountUser(accountUser.id)
      }
    };
    if (openProgramsByAccountUser?.length == 0 || jobOrdersByAccountUser?.length == 0) { return 0.00 };
    openProgramsByAccountUser?.forEach(p => {
      p.programAllowances.forEach(j => {
        if (j.)
      })
    })
   return 0.00;

  } */

  // ---------------------------------------------------------------------------------------
  // Program Access rows — the per-PROGRAM view of a user's Program User Groups.
  //
  // ProgramAccessItem is keyed by (program x PUG). The invariant that decides whether a user
  // can actually order is keyed by PROGRAM ALONE: at most one of the user's PUGs may be
  // "allowed" on it. Collapsing the items down to one row per program is what lets the UI
  // state that invariant instead of leaving the admin to infer it. See
  // Docs/program-user-group-access.md.
  // ---------------------------------------------------------------------------------------

  /**
   * Collapses ProgramAccessItems (one per program x PUG) into one row per Program, deciding
   * each program's access state and which PUG — if any — governs it.
   */
  public buildProgramAccessRows(items: ProgramAccessItem[]): ProgramAccessRow[] {
    const byProgram = _.groupBy(items, (item) => item.program.id);

    const rows = Object.values(byProgram).map((programItems) => {
      const first = programItems[0];
      const program = first.program;

      const choices: PugAccessChoice[] = programItems.map((item) => {
        const isPugActive = item.programUserGroupActiveStatusId === ActiveStatusEnum.Active;
        return {
          programUserGroupId: item.programUserGroupId,
          programUserGroupName: item.programUserGroupName,
          isPugActive,
          pugStatusName: item.programUserGroupStatus,
          isExcluded: item.isExcluded,
          // "Allowed" mirrors isAllowed() in buildProgramUserItemsExpanded exactly: the PUG is
          // Active and the user carries no exclusion for it on this program.
          isAllowed: isPugActive && !item.isExcluded,
          detail: this.describePugChoice(item),
        };
      });

      const allowed = choices.filter((c) => c.isAllowed);
      const { state, statusLabel } = this.calcProgramAccessState(first, choices, allowed);

      return {
        programId: program.id,
        programName: program.name,
        programTypeLabel: first.programType,
        choices: _.sortBy(choices, (c) => c.programUserGroupName),
        // Only meaningful when exactly one PUG is allowed. Blocked rows deliberately show a
        // count instead of a name — naming one of two winners is the confusion we are fixing.
        routedThroughName:
          allowed.length === 1 ? allowed[0].programUserGroupName : allowed.length === 0 ? '—' : `${allowed.length} groups`,
        state,
        statusLabel,
        // A single PUG can still be swapped for a different one, and an excluded program can be
        // re-included, so the chooser stays reachable on healthy rows too — that is how "None"
        // (a legitimate configuration) is set in the first place. Date-driven states have
        // nothing to choose.
        canChoose: state !== 'Expired' && choices.length > 0,
      } as ProgramAccessRow;
    });

    return _.sortBy(rows, (r) => r.programName);
  }

  /**
   * Precedence for a program's access state. Order matters and is deliberate:
   *
   *  Dead programs first — Expired or Inactive. The program will never route another order,
   *                  so a configuration problem on it is noise, and flagging it in red trains
   *                  admins to ignore red. Verified against real data: every blocked pair on an
   *                  Inactive program in the dev DB is historical, while the ones on HELD
   *                  programs have future end dates and become live blocks on reactivation.
   *                  Hold is therefore deliberately NOT treated as dead.
   *  Blocked next  — the invariant violation is the only always-wrong state and the only one
   *                  that needs to shout, including on a program that has not started yet.
   *  No access     — zero allowed PUGs. Split by cause, because the fixes differ entirely.
   *  Dates         — not started / not yet open for this user.
   */
  private calcProgramAccessState(
    item: ProgramAccessItem,
    choices: PugAccessChoice[],
    allowed: PugAccessChoice[],
  ): { state: ProgramAccessState; statusLabel: string } {
    const program = item.program;

    if (item.isExpired) {
      return { state: 'Expired', statusLabel: 'Expired' };
    }
    if (program.activeStatusId === ActiveStatusEnum.Inactive) {
      return { state: 'ProgramInactive', statusLabel: 'Program inactive' };
    }

    if (allowed.length > 1) {
      return { state: 'Blocked', statusLabel: `Blocked — ${allowed.length} groups` };
    }

    if (allowed.length === 0) {
      // Excluded-on-purpose vs the group being switched off are different problems with
      // different fixes; "No Access" used to collapse both into one red word.
      const anyActivePug = choices.some((c) => c.isPugActive);
      return anyActivePug
        ? { state: 'ExcludedByDesign', statusLabel: 'Excluded — by design' }
        : { state: 'GroupInactive', statusLabel: 'No access — group inactive' };
    }

    if (program.startDate != null && program.startDate.getTime() > Date.now()) {
      return { state: 'NotStarted', statusLabel: `Starts ${DateFns.fmtDate(program.startDate)}` };
    }

    // The user's own join-date gate — the program is open, but not to them yet.
    if (item.joinDate != null && program.availableAfterJoinDateDays) {
      const opensOn = DateFns.dateAdd(item.joinDate, program.availableAfterJoinDateDays, 'days');
      if (opensOn.getTime() > Date.now()) {
        return { state: 'NotYetOpen', statusLabel: `Opens ${DateFns.fmtDate(opensOn)}` };
      }
    }

    return { state: 'CanOrder', statusLabel: 'Can order' };
  }

  /**
   * The one-line summary under each option in the chooser. This is what makes picking a winner
   * an informed decision rather than a coin flip — these are the things that actually differ
   * between two groups on the same program.
   */
  private describePugChoice(item: ProgramAccessItem): string {
    const parts: string[] = [];
    if (item.isAllowanceProgram && item.allowanceAmt != null) {
      parts.push(`${UtilFns.fmtCurrency(item.allowanceAmt)} allowance`);
    }
    if (item.allowanceNames.length > 0) {
      parts.push(item.allowanceNames.join(', '));
    }
    if (item.issuanceCategories.length > 0) {
      const n = item.issuanceCategories.length;
      parts.push(`${n} issuance ${n === 1 ? 'category' : 'categories'}`);
    }
    if (item.adminNames.length > 0) {
      parts.push(`approved by ${item.adminNames.join(', ')}`);
    }
    return parts.join(' · ');
  }
}
